import { useEffect, useState, ReactNode } from "react"

import { useNavigate } from "react-router-dom"

import { ContentObjectItem, ContentObjectStatus, InteractionRef } from "@vertesia/common"
import { EllipsisVertical, PanelRightClose, PanelRightOpen, RotateCw, Share2 } from "lucide-react"
import { toast } from "sonner"

import { CollectionButton } from "@components/buttons/CollectionButton"
import { DownloadDocumentButton } from "@components/buttons/DownloadDocumentButton"
import { GenerateMetadataButton } from "@components/buttons/GenerateMetadataButton"
import { DocumentDisplay } from "@components/displays/DocumentDisplay"
import { ChatPanel } from "@components/panels/ChatPanel"
import { DocumentEmbeddingsPanel } from "@components/panels/DocumentEmbeddingsPanel"
import { NoAvailablePanel } from "@components/panels/NoAvailablePanel"
import { PropertiesPanel } from "@components/panels/PropertiesPanel"
import { ServicesPanel } from "@components/panels/ServicesPanel"

import { LAST_PANEL_STATE } from "@constants/cookies"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"

interface DocumentPanelProps {
    children?: ReactNode
    id: string
}
export function DocumentPanel({ id, children }: Readonly<DocumentPanelProps>) {
    const { client, isLoading, project } = useUserSession()
    const navigate = useNavigate()

    const [isPanelOpen, setIsPanelOpen] = useState(localStorage.getItem(LAST_PANEL_STATE) === "open")
    const [openActions, setOpenActions] = useState(false)

    const [document, setDocument] = useState<ContentObjectItem | undefined>(undefined)
    const [documentUrl, setDocumentUrl] = useState<string | undefined>(undefined)
    const [panelState, setPanelState] = useState("properties")
    const [chatInteractionId, setChatInteractionId] = useState<string | undefined>(undefined)
    const [documentInteractions, setDocumentInteractions] = useState<InteractionRef[]>([])

    const navigateTo404 = () => {
        navigate("/404")
    }

    const searchDocument = () => {
        if (!client || isLoading) {
            return
        }

        client.objects
            .retrieve(id)
            .then((response) => {
                if (response) {
                    setDocument(response)
                } else {
                    navigateTo404()
                }
            })
            .catch(() => {
                navigateTo404()
            })
    }

    const handlePanelStateChange = () => {
        const current = isPanelOpen
        localStorage.setItem(LAST_PANEL_STATE, current ? "closed" : "open")
        setIsPanelOpen(!current)
    }

    const refresh = () => {
        searchDocument()
    }

    const share = () => {
        if (!document || !project) {
            return
        }

        navigator.clipboard.writeText(`${window.location.origin}/document/${document.id}?a=${project.account}&p=${project.id}`)
        toast.success("Link copied to clipboard", {
            duration: 1000,
        })
        setOpenActions(false)
    }

    useEffect(() => {
        if (!client || isLoading || document) {
            return
        }

        searchDocument()
    }, [id])

    useEffect(() => {
        if (!client || isLoading || !document) {
            return
        }

        if (document.content) {
            client.objects.getDownloadUrl(document.content.source!).then((response) => {
                setDocumentUrl(response.url)
            })
        }

        if (!chatInteractionId) {
            client.interactions
                .list({ query: { tags: ["chat"] } })
                .then((response) => {
                    if (response.length > 0) {
                        setChatInteractionId(response[0].id)
                    } else {
                        setChatInteractionId(undefined)
                    }
                })
                .catch(() => {
                    setChatInteractionId(undefined)
                })
        }

        if (!documentInteractions.length) {
            client.interactions
                .list({ query: { tags: ["document"] } })
                .then((response) => {
                    setDocumentInteractions(response)
                })
                .catch(() => {
                    setDocumentInteractions([])
                })
        }
    }, [document])

    if (!document) {
        return <div></div>
    }

    return (
        <div className="flex flex-col h-full" style={{ all: "initial", fontFamily: "inherit", color: "inherit" }}>
            <div className="w-full flex flex-row gap-2 items-center justify-between p-2 border-b">
                <div className="text-2xl font-semibold truncate">{document?.properties?.title || document?.name || document?.id}</div>
                <div className="flex flex-row gap-2">
                    <DownloadDocumentButton object={document} variant="default" size="default" text="Download" />
                    <Button onClick={refresh}>
                        <RotateCw /> <span>Refresh</span>
                    </Button>
                    <Popover open={openActions} onOpenChange={setOpenActions}>
                        <PopoverTrigger asChild>
                            <Button>
                                <EllipsisVertical />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent side="bottom" align="end" className="p-1 flex flex-col gap-1 w-[250px]">
                            <Button onClick={share} variant="ghost" className="text-left justify-start w-full">
                                <Share2 />
                                <span>Share Document</span>
                            </Button>
                            <CollectionButton document={document} onActionDone={() => setOpenActions(false)} />
                            <GenerateMetadataButton document={document} onActionDone={() => setOpenActions(false)} onGenerationDone={refresh} />
                        </PopoverContent>
                    </Popover>
                    {children}
                </div>
            </div>
            <div className="flex flex-row h-[calc(100vh-110px)]">
                <div className={`${isPanelOpen ? "w-[70%]" : "w-full"} h-full bg-gray-50 flex items-center justify-center`}>{documentUrl ? <DocumentDisplay documentId={document.id} name={document.name} type={document.content.type!} url={documentUrl} /> : "No Document content Source"}</div>
                <div className={`${isPanelOpen ? "w-[30%]" : ""} h-full bg-gray-100 border-l border-gray-300`}>
                    {isPanelOpen && (
                        <div className="flex flex-col h-full">
                            <div className="flex flex-row gap-1 items-center justify-between">
                                <div className="flex flex-row gap-1 m-2 overflow-x-auto">
                                    <Button onClick={() => setPanelState("properties")} variant="ghost" className={`${panelState === "properties" ? "bg-white" : ""} hover:opacity-60`}>
                                        Properties
                                    </Button>
                                    <Button onClick={() => setPanelState("similar")} variant="ghost" className={`${panelState === "similar" ? "bg-white" : ""} hover:opacity-60`}>
                                        Similar
                                    </Button>
                                    {chatInteractionId && (
                                        <Button onClick={() => setPanelState("chat")} variant="ghost" className={`${panelState === "chat" ? "bg-white" : ""} hover:opacity-60`}>
                                            Chat
                                        </Button>
                                    )}
                                    {documentInteractions.length > 0 && (
                                        <Button onClick={() => setPanelState("services")} variant="ghost" className={`${panelState === "services" ? "bg-white" : ""} hover:opacity-60`}>
                                            Services
                                        </Button>
                                    )}
                                </div>
                                <Button variant="ghost" onClick={handlePanelStateChange} className="m-2 size-10">
                                    <PanelRightClose />
                                </Button>
                            </div>
                            {panelState === "properties" && <div className="flex-1 overflow-y-auto">{document?.status === ContentObjectStatus.completed ? document?.properties ? <PropertiesPanel value={document.properties} /> : <NoAvailablePanel>No Properties available.</NoAvailablePanel> : <NoAvailablePanel>Properties are actively being generated, check back momentarily.</NoAvailablePanel>}</div>}
                            {panelState === "similar" && <div className="flex-1 overflow-y-auto">{document ? <DocumentEmbeddingsPanel document={document} /> : <NoAvailablePanel>No similar content available for this document.</NoAvailablePanel>}</div>}
                            {panelState === "chat" && <div className="flex-1 overflow-y-auto">{document ? <ChatPanel document={document} interactionId={chatInteractionId!} /> : <NoAvailablePanel>No Chat available for this document.</NoAvailablePanel>}</div>}
                            {panelState === "services" && <div className="flex-1 overflow-y-auto">{<ServicesPanel document={document} interactions={documentInteractions} />}</div>}
                        </div>
                    )}
                    {!isPanelOpen && (
                        <Button variant="ghost" onClick={handlePanelStateChange} className="m-2 size-10">
                            <PanelRightOpen />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}
